import type * as api from "../api/client";
import { formatGold, formatVoidJuice } from "../logic/currency";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";

// Daily-challenge stepper panel. Pure presentation: parent passes the
// challenge state and an `onPickChallenge` callback the active node
// fires when its FIGHT button is clicked. When `onReset` is provided
// (NpcView only passes it when Admin Mode is enabled), a Reset button
// appears top-right and wipes today's completion rows for this NPC.

interface Props {
  challenges: api.NpcChallengeSet;
  resetsInSeconds: number;
  onPickChallenge: (i: number) => void;
  /** Admin-only. When present, the panel renders a Reset button at
   *  the top-right that wipes today's completion rows for this NPC.
   *  Gating lives in the parent (NpcView) — the panel just shows
   *  the button if the callback is supplied. */
  onReset?: () => void;
}

export function ChallengePanel({
  challenges,
  resetsInSeconds,
  onPickChallenge,
  onReset,
}: Props) {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  return (
    <section
      style={{
        ...simplePanelStyle(theme),
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          DAILY CHALLENGES
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              color: theme.inkFaint,
              letterSpacing: theme.letterSpacing,
            }}
          >
            RESETS IN {formatHMS(resetsInSeconds)} ·{" "}
            {challenges.completed_today}/{challenges.entries.length} DONE
          </div>
          {onReset && (
            <button
              onClick={onReset}
              {...withBtnStyle(defaultBtn(theme), {
                background: E32.oxblood,
                color: E32.parchment,
                borderColor: E32.ruby,
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                letterSpacing: theme.letterSpacing,
                padding: "4px 8px",
              })}
              title="Admin: wipe today's completion rows for this NPC."
            >
              RESET (ADMIN)
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          // Side-by-side stepper on desktop; stacked on narrow so each
          // node has room for its title + reward pill + button.
          gridTemplateColumns: narrow
            ? "1fr"
            : `repeat(${challenges.entries.length}, 1fr)`,
          // Stacked vertically each node is a tall card (number, title,
          // reward pills, button); 12px between them reads as cramped.
          // Give the single-column layout more breathing room.
          gap: narrow ? 28 : 12,
        }}
      >
        {challenges.entries.map((entry) => (
          <ChallengeNode
            key={entry.index}
            entry={entry}
            onClick={() => {
              if (entry.state === "available") onPickChallenge(entry.index);
            }}
          />
        ))}
      </div>
    </section>
  );
}

function ChallengeNode({
  entry,
  onClick,
}: {
  entry: api.NpcChallengeEntry;
  onClick: () => void;
}) {
  const theme = pixelTheme;
  // Color mapping mirrors Claude Design's PipScreen exactly.
  const colors = {
    completed: { bg: theme.accent, border: E32.ink, text: E32.parchment },
    available: { bg: E32.amber, border: E32.ink, text: E32.ink },
    locked: { bg: theme.panel, border: theme.panelEdge, text: theme.inkFaint },
  }[entry.state];

  const stateLabelColor =
    entry.state === "available"
      ? theme.accent
      : entry.state === "completed"
        ? E32.forest
        : theme.inkFaint;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        opacity: entry.state === "locked" ? 0.7 : 1,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          background: colors.bg,
          border: `3px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: theme.fontDisplay,
          fontSize: 18,
          color: colors.text,
        }}
      >
        {entry.state === "completed" ? "✓" : entry.index + 1}
      </div>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 8,
          letterSpacing: theme.letterSpacing,
          color: stateLabelColor,
        }}
      >
        {entry.state.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: theme.fontBody,
          fontSize: 14,
          color: entry.state === "locked" ? theme.inkFaint : theme.ink,
          textAlign: "center",
          lineHeight: 1.3,
          padding: "0 4px",
          minHeight: 36,
        }}
      >
        {entry.title}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "center",
        }}
      >
        {entry.rewards.map((r, i) => (
          <RewardPill key={i} reward={r} dim={entry.state !== "available"} />
        ))}
      </div>
      <button
        onClick={onClick}
        disabled={entry.state !== "available"}
        {...primaryBtn(theme, entry.state !== "available")}
      >
        {entry.state === "completed"
          ? "DONE TODAY"
          : entry.state === "available"
            ? "FIGHT"
            : "LOCKED"}
      </button>
    </div>
  );
}

function RewardPill({
  reward,
  dim,
}: {
  reward: api.NpcChallengeReward;
  dim: boolean;
}) {
  const theme = pixelTheme;

  if (reward.kind === "flag") {
    // Flags are arbitrary world-state writes. Authors supply a
    // human-readable `label:` on the reward (e.g. "Goblin girl's
    // freedom") — the server requires it, so it's always present.
    return (
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: E32.burntOrange,
          letterSpacing: theme.letterSpacing,
          opacity: dim ? 0.55 : 1,
        }}
      >
        {reward.label.toUpperCase()}
      </span>
    );
  }

  if (reward.kind === "item") {
    return (
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          color: E32.amber,
          letterSpacing: theme.letterSpacing,
          opacity: dim ? 0.55 : 1,
        }}
      >
        {`${reward.count}× ${reward.name}`.toUpperCase()}
      </span>
    );
  }

  const isJuice = reward.currency === "void_juice";
  return (
    <span
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        color: isJuice ? E32.mulberry : E32.oxblood,
        letterSpacing: theme.letterSpacing,
        opacity: dim ? 0.55 : 1,
      }}
    >
      {isJuice ? formatVoidJuice(reward.amount) : `${formatGold(reward.amount)} GOLD`}
    </span>
  );
}

function formatHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}
